import { SupportedPlatforms } from '@smg-real-estate/domus';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { trackPromoteAccountBannerClick } from '@/components/ListingDetails/ListingDetailsTracking';
import { isUserLoggedIn, loginWithRedirect } from '@/helpers/Authentication/authClient';
import { RedirectAction } from '@/helpers/Authentication/redirect';
import { LoginTargetAction, ReferringFeature } from '@/helpers/Authentication/types';
import { isPlatformIs24 } from '@/helpers/platformDetection';

export function usePromotionalBanner() {
  const { t } = useI18n();
  const isUserAuthenticated = ref(false);
  const promotionalCardImage = ref<string | undefined>(undefined);
  const isPlatformIs24Flag = isPlatformIs24();

  const showPromoteAccountCreationBanner = computed(() => {
    return !isUserAuthenticated.value;
  });

  const platform = computed((): SupportedPlatforms => {
    return isPlatformIs24Flag ? SupportedPlatforms.is24 : SupportedPlatforms.hg;
  });

  const badgeText = computed((): string => {
    return isPlatformIs24Flag
      ? t('promoteAccountBanner.is24.badgeText').toString()
      : t('promoteAccountBanner.badgeText').toString();
  });

  const promotionalBannerDescription = computed((): string => {
    return isPlatformIs24Flag
      ? t('promoteAccountBanner.is24.description').toString()
      : t('promoteAccountBanner.description').toString();
  });

  const promotionalBannerButtonText = computed((): string => {
    return isPlatformIs24Flag
      ? t('promoteAccountBanner.is24.CTA').toString()
      : t('promoteAccountBanner.CTA').toString();
  });

  const promotionalBannerTitle = computed((): string => {
    return isPlatformIs24Flag
      ? t('promoteAccountBanner.is24.title').toString()
      : t('promoteAccountBanner.title').toString();
  });

  const checkAuthenticationStatus = async () => {
    isUserAuthenticated.value = await isUserLoggedIn();
  };

  const handleRegister = async () => {
    trackPromoteAccountBannerClick();
    await loginWithRedirect(
      LoginTargetAction.signup,
      RedirectAction.PROMOTE_ACCOUNT_CREATION_PDP,
      { eventAction: 'auth_sign_up' },
      ReferringFeature.ACCOUNT_PROMOTION_PDP,
    );
  };

  const setPromotionalCardImage = async (): Promise<void> => {
    const image = await import(
      isPlatformIs24Flag
        ? '@/assets/promotional-card-pdp/promote-account-is24.png'
        : '@/assets/promotional-card-pdp/promote-account-hg.svg?url'
    );
    promotionalCardImage.value = image.default;
  };

  onMounted(() => {
    checkAuthenticationStatus();
  });

  return {
    isUserAuthenticated,
    promotionalCardImage,
    showPromoteAccountCreationBanner,
    platform,
    badgeText,
    promotionalBannerDescription,
    promotionalBannerButtonText,
    promotionalBannerTitle,
    handleRegister,
    setPromotionalCardImage,
  };
}
